import { useCallback } from 'react';
import { cn } from '@/shared/lib/utils/classnames';
import { useYoutubePlayerStore } from '@/shared/store/youtube-player';
import type { BadgeBlock as BadgeBlockType } from '@/shared/types/blocks';

interface BadgeBlockProps extends Omit<BadgeBlockType, 'type' | 'id'> {
  /** 뱃지 크기: sm(12px), md(14px) */
  size?: 'sm' | 'md';
  /** 연결된 YouTube 영상 ID (클릭 시 해당 시간으로 이동) */
  linkedVideoId?: string;
}

/**
 * BadgeBlock - 단순 뱃지 (타임스탬프 등)
 *
 * Figma 디자인 기준:
 * - filled: 진한 파란색 배경 (#0084ff) + 흰색 텍스트 (카드 내 타임스탬프)
 * - tint: 연한 파란색 배경 (#e6f1ff) + 파란색 텍스트 (스크립트 타임스탬프)
 * - outline: 테두리만 있는 기본 스타일 (Label/Smaller B: 12px bold, lh 135%)
 *
 * 타임스탬프 클릭 시 영상 재생:
 * - seconds와 linkedVideoId가 있으면 클릭 가능한 버튼으로 렌더링
 * - 클릭 시 해당 시간대로 영상 이동 및 재생
 */
export function BadgeBlock({
  text,
  variant = 'filled',
  size = 'sm',
  seconds,
  linkedVideoId,
}: BadgeBlockProps) {
  const { requestSeek } = useYoutubePlayerStore();

  const isClickable = seconds !== undefined && linkedVideoId !== undefined;

  const handleClick = useCallback(() => {
    if (isClickable) {
      requestSeek(linkedVideoId, seconds);
    }
  }, [isClickable, linkedVideoId, seconds, requestSeek]);

  const variantStyles = {
    filled: 'bg-primary text-white',
    tint: 'bg-[#e6f1ff] text-primary',
    // Figma: border #d9dde0, text black
    outline: 'border border-[#d9dde0] text-black bg-transparent',
    // Corporate Scouter 에이전트용 (Figma 디자인 기준)
    success: 'bg-[#30c353] text-white',
    warning: 'bg-amber-500 text-white',
    danger: 'bg-red-500 text-white',
    muted: 'bg-gray-100 text-gray-600',
  };

  // Figma Typography: Label/Smaller B (12px), Label/Small B (14px)
  const sizeStyles = {
    sm: 'label-smaller-b', // 12px bold, lh 1.35
    md: 'label-small-b', // 14px bold, lh 1.35
  };

  const baseClassName = cn(
    'inline-flex items-center justify-center shrink-0 whitespace-nowrap',
    // Figma: 콘텐츠 길이에 맞춤 (fit-content), flex 부모에서 stretch 방지
    'px-[8px] py-[2px] rounded-[4px] w-fit self-start',
    variantStyles[variant],
    sizeStyles[size],
  );

  // 클릭 가능한 타임스탬프는 버튼으로 렌더링
  if (isClickable) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={cn(baseClassName, 'cursor-pointer hover:opacity-80 transition-opacity')}
      >
        {text}
      </button>
    );
  }

  return <span className={baseClassName}>{text}</span>;
}
